import React, {useRef, useState, useEffect} from 'react';

import {BlockButton} from './ToolbarComponents/BlockButton.react';
import {MarkButton} from './ToolbarComponents/MarkButton.react';
import {FontDropdown} from './ToolbarComponents/FontDropdown.react';
import {InOutdentButton} from './ToolbarComponents/InOutdentButton.react';
import {LinkButton} from './ToolbarComponents/LinkButton.react';
import {AddMediaButton} from './ToolbarComponents/Media/AddMediaButton.react';

const TOTAL_INTERACTIVE_ELEMENTS = 13;
const TOOLBAR_CONTROL_SELECTOR = '[data-toolbar-control]';

const getControls = (toolbar: HTMLElement | null) =>
  Array.from(toolbar?.querySelectorAll<HTMLElement>(TOOLBAR_CONTROL_SELECTOR) ?? []);

const Toolbar = ({disabled = false}: {disabled?: boolean}) => {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [activeCursor, setActiveCursor] = useState<number>(0);
  const hasMovedCursor = useRef(false);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!toolbarRef.current?.contains(event.target as Node)) {
      return;
    }
    if (event.key === 'ArrowRight') {
      hasMovedCursor.current = true;
      setActiveCursor((oldCursor) => (oldCursor + 1) % TOTAL_INTERACTIVE_ELEMENTS);
    } else if (event.key === 'ArrowLeft') {
      hasMovedCursor.current = true;
      setActiveCursor(
        (oldCursor) => (oldCursor - 1 + TOTAL_INTERACTIVE_ELEMENTS) % TOTAL_INTERACTIVE_ELEMENTS
      );
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    const index = getControls(toolbarRef.current).indexOf(event.target);
    if (index !== -1) {
      hasMovedCursor.current = true;
      setActiveCursor(index);
    }
  };

  // Only once the student has moved the cursor: focusing on mount pulls focus
  // into a toolbar below the fold, scrolling the prompt out of view (#21341).
  useEffect(() => {
    if (!hasMovedCursor.current) {
      return;
    }
    getControls(toolbarRef.current)[activeCursor]?.focus();
  }, [activeCursor]);

  const controlProps = (index: number) => ({
    tabIndex: index === activeCursor ? 0 : -1,
    'data-toolbar-control': true
  });

  return (
    <div
      ref={toolbarRef}
      role='toolbar'
      className='written-submission__toolbar'
      aria-label='Text Formatting Toolbar'
      aria-orientation='horizontal'
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
    >
      <div className='written-submission__toolbar-group' role='group' aria-label='Text Style'>
        <FontDropdown disabled={disabled} {...controlProps(0)} />
        <MarkButton icon='bold' type='bold' disabled={disabled} {...controlProps(1)} />
        <MarkButton icon='italic' type='italic' disabled={disabled} {...controlProps(2)} />
        <MarkButton icon='underline' type='underline' disabled={disabled} {...controlProps(3)} />
      </div>
      <div
        className='written-submission__toolbar-group'
        role='group'
        aria-label='List and Indentation'
      >
        <Divider />
        <BlockButton icon='list-ol' type='numbered-list' disabled={disabled} {...controlProps(4)} />
        <BlockButton icon='list-ul' type='bulleted-list' disabled={disabled} {...controlProps(5)} />
        <InOutdentButton icon='indent' type='indent' disabled={disabled} {...controlProps(6)} />
        <InOutdentButton icon='outdent' type='outdent' disabled={disabled} {...controlProps(7)} />
      </div>
      <div className='written-submission__toolbar-group' role='group' aria-label='Text Formatting'>
        <Divider />
        <MarkButton
          icon='superscript'
          type='superscript'
          disabled={disabled}
          {...controlProps(8)}
        />
        <MarkButton icon='subscript' type='subscript' disabled={disabled} {...controlProps(9)} />
        <MarkButton icon='code' type='code' disabled={disabled} {...controlProps(10)} />
      </div>
      <div className='written-submission__toolbar-group' role='group' aria-label='Insert'>
        <Divider />
        <LinkButton disabled={disabled} {...controlProps(11)} />
        <AddMediaButton disabled={disabled} {...controlProps(12)} />
      </div>
    </div>
  );
};

const Divider = () => {
  return (
    <div
      className='written-submission__toolbar--divider'
      role='separator'
      aria-orientation='vertical'
    >
      <div className='written-submission__toolbar--divider-inside' />
    </div>
  );
};

export default Toolbar;
